import { DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Col, FormGroup, Input, Label, Table } from 'reactstrap';
import { MaterialIcon } from '..';
import { OptionInternal, OptionSelectValue, SourceConfig } from '../../../shared/options';
import { useTranslation } from '../../translations';
import { useNullishNumericInputState, useToggle } from '../../utils';
import { Currency } from '../Currency';
import { OptionEditorSelectAdd } from './OptionEditorSelectAdd';

interface OptionEditorSelectProps {
  readonly option: OptionInternal;
  readonly isMulti?: boolean;
  readonly setOption: Dispatch<SetStateAction<OptionInternal>>;
  readonly setIsEditing: (state: boolean) => void;
}

function findIndex(pages: OptionSelectValue[], id: string) {
  return pages.findIndex((p) => p.value === id);
}

const emptyArr: never[] = [];

export const OptionEditorSelect: FC<OptionEditorSelectProps> = ({
  option,
  setOption,
  isMulti,
  setIsEditing,
}) => {
  const { ts } = useTranslation();
  const [isCreate, toggleCreate, setIsCreate] = useToggle(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [max, setMax] = useNullishNumericInputState(option.id === 0 ? 50 : option.max);
  const [editKey, setEditKey] = useState<string | null>(null);
  const selectValues = option.selectValues ?? emptyArr;
  const config = SourceConfig[option.source];

  useEffect(() => {
    setOption((old) => ({ ...old, max }));
  }, [setOption, max]);

  const updateSelectValues = useCallback(
    (values: SetStateAction<OptionSelectValue[]>) => {
      setOption((old) => ({
        ...old,
        selectValues: typeof values === 'function' ? values(old.selectValues ?? []) : values,
      }));
    },
    [setOption],
  );

  const deleteSelectOption = useCallback(
    (value: string) => {
      updateSelectValues((old) => old.filter((so) => so.value !== value));
    },
    [updateSelectValues],
  );

  const onEdit = useCallback(
    (value: OptionSelectValue) => {
      const editedValues: OptionSelectValue[] = selectValues.map((item) => {
        if (item.value === value.value) {
          return value;
        }

        return item;
      });

      updateSelectValues(editedValues);
      setEditKey(null);
      setIsEditing(false);
    },
    [selectValues, setIsEditing, updateSelectValues],
  );

  const onAddNew = useCallback(
    (value: OptionSelectValue) => {
      if (selectValues.some((t) => t.value === value.value)) {
        toast.error(ts('option_editor_select.the_options_value_need_to'));
        return;
      }

      updateSelectValues((old) => old.concat(value));
      setIsCreate(false);
    },
    [selectValues, setIsCreate, ts, updateSelectValues],
  );

  const onDragEnd = useCallback(
    ({ over }: DragEndEvent) => {
      setActiveId(null);
      if (over) {
        updateSelectValues((old) => {
          const activeIndex = activeId ? findIndex(old, activeId) : -1;
          const overIndex = findIndex(old, over.id as string);

          if (activeIndex !== overIndex) {
            return arrayMove(old, activeIndex, overIndex);
          }

          return old;
        });
      }
    },
    [activeId, setActiveId, updateSelectValues],
  );

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      setActiveId(active.id as string);
    },
    [setActiveId],
  );

  return (
    <>
      <Col lg={6} xs={12}>
        {isMulti && (
          <FormGroup>
            <Label for="options">
              {ts('option_editor_select.max_options')} <small>({ts('common.optional')})</small>
            </Label>
            <Input defaultValue={max ?? ''} name="max" onChange={setMax} type="number" />
          </FormGroup>
        )}
      </Col>
      <Col lg={6} xs={12} />
      <Col xs={12}>
        <DndContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
          <SortableContext items={selectValues.map((t) => t.value)}>
            <Label for="options">{ts('option_editor_select.select_options')}</Label>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th>{ts('common.value')}</th>
                  <th>{ts('option_editor_select.display_name_optional')}</th>
                  {config.allowPrice && <th>{ts('option_editor_select.price_modifier')}</th>}
                  <th style={{ width: '70px', textAlign: 'right' }}>
                    <Button
                      color={isCreate ? 'danger' : 'success'}
                      id={`addOptionValue${isCreate ? 'Cancel' : ''}`}
                      onClick={toggleCreate}
                      outline={!isCreate}
                    >
                      <MaterialIcon className="align-middle" name={isCreate ? 'close' : 'add'} />
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isCreate && (
                  <OptionEditorSelectAdd allowPrice={config.allowPrice} onCreate={onAddNew} />
                )}
                {selectValues.map((opt) => {
                  return opt.value === editKey ? (
                    <OptionEditorSelectAdd
                      allowPrice={config.allowPrice}
                      existing={opt}
                      onCreate={onEdit}
                    />
                  ) : (
                    <SelectItem
                      allowPrice={config.allowPrice}
                      deleteItem={deleteSelectOption}
                      enableEdit={(value) => {
                        setEditKey(value);
                        setIsEditing(true);
                      }}
                      item={opt}
                      key={opt.value}
                    />
                  );
                })}
              </tbody>
            </Table>
          </SortableContext>
        </DndContext>
      </Col>
    </>
  );
};

interface SelectItemProps {
  readonly item: OptionSelectValue;
  readonly allowPrice: boolean;
  readonly enableEdit: (value: string) => void;
  readonly deleteItem: (value: string) => void;
}

const SelectItem: FC<SelectItemProps> = ({ item, enableEdit, deleteItem, allowPrice }) => {
  const { attributes, isDragging, listeners, setNodeRef, transition, transform } = useSortable({
    id: item.value,
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <tr id={`selectValue${item.value}`} ref={setNodeRef} style={style}>
      <td style={{ width: '50px' }}>
        <div className="handlebar" {...attributes} {...listeners} style={{ float: 'left' }}>
          <MaterialIcon name="drag_indicator" />
        </div>
      </td>
      <td>{item.value}</td>
      <td>{item.displayName}</td>
      {allowPrice && (
        <td>
          <Currency value={item.priceModifier} />
        </td>
      )}
      <td className="text-end">
        <MaterialIcon
          className="pointer edit"
          name="edit"
          onClick={() => {
            enableEdit(item.value);
          }}
        />
        <MaterialIcon
          className="pointer delete"
          name="delete"
          onClick={() => {
            deleteItem(item.value);
          }}
        />
      </td>
    </tr>
  );
};
